import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, createComponent,
} from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { SystemApp } from '../constans/SystemConfigurations';
import { ApiUrl } from '../shared/models/apiurl.models';

/**
 * In-site inquiries: the buyer writes to the seller without leaving the site.
 * The backend stores the message in both inboxes ("פניות" in the personal
 * area) and, when the seller opted in, relays it to the seller's WhatsApp
 * from the platform's business number. Phones are never exchanged.
 */
export interface InquiryOptions {
  mode: 'relay' | 'link';
  login_required: boolean;
  own_ad: boolean;
  available: boolean;
  max_length: number;
  default_text: string;
  seller_name: string;
  seller_on_whatsapp: boolean;
  whatsapp_enabled: boolean;
  me_on_whatsapp: boolean;
}

export interface InquiryMessage {
  id: number;
  mine: boolean;
  text: string;
  channel: 'web' | 'app' | 'whatsapp';
  at: string;
  wa_status: '' | 'skipped' | 'queued' | 'sent' | 'delivered' | 'read' | 'failed';
}

export interface InquirySummary {
  id: number;
  role: 'buyer' | 'seller';
  product: { id: number | null; name: string; image: string | null; live: boolean };
  other: { id: number; name: string; avatar: string | null };
  last_message: { text: string; mine: boolean; at: string } | null;
  unread: number;
  last_message_at: string;
}

export interface InquiryThread extends InquirySummary {
  messages: InquiryMessage[];
  other_on_whatsapp: boolean;
}

export interface WhatsAppConsent {
  available: boolean;
  opted_in: boolean;
  phone: string;
  opted_in_at: string | null;
}

export interface InquiryTarget {
  adId: number;
  adName: string;
  /** the listing's contact phone — only used when the feature is off (wa.me) */
  phone?: string;
}

export interface InquiryError {
  code: string;
  detail: string;
}

@Injectable({ providedIn: 'root' })
export class InquiryService {
  private base = SystemApp.base_url_api + '/client-api/inquiries/';
  /** unread count for the nav badge; refreshed by the inbox page and after sends */
  readonly unread$ = new BehaviorSubject<number>(0);
  private dialogRef?: ComponentRef<any>;

  constructor(
    private http: HttpClient,
    private apiUrl: ApiUrl,
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
  ) {}

  get loggedIn(): boolean {
    return !!this.apiUrl.getJWTToken();
  }

  private headers(): HttpHeaders {
    const token = this.apiUrl.getJWTToken();
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = token;
    return new HttpHeaders(h);
  }

  options(adId: number): Observable<InquiryOptions> {
    return this.http.get<InquiryOptions>(`${this.base}options/${adId}/`, { headers: this.headers() });
  }

  send(adId: number, text: string): Observable<{ inquiry_id: number; message: InquiryMessage; seller_on_whatsapp: boolean }> {
    return this.http.post<any>(`${this.base}send/`, { ad_id: adId, text, channel: 'web' },
      { headers: this.headers() });
  }

  list(role: 'all' | 'buying' | 'selling' = 'all'): Observable<{ results: InquirySummary[]; unread: number }> {
    return this.http.get<{ results: InquirySummary[]; unread: number }>(
      `${this.base}?role=${role}`, { headers: this.headers() },
    ).pipe(tap(r => this.unread$.next(r.unread || 0)));
  }

  thread(id: number): Observable<InquiryThread> {
    return this.http.get<InquiryThread>(`${this.base}${id}/`, { headers: this.headers() });
  }

  reply(id: number, text: string): Observable<InquiryMessage> {
    return this.http.post<InquiryMessage>(`${this.base}${id}/reply/`, { text, channel: 'web' },
      { headers: this.headers() });
  }

  refreshUnread(): void {
    if (!this.loggedIn) { this.unread$.next(0); return; }
    this.http.get<{ unread: number }>(`${this.base}unread/`, { headers: this.headers() }).pipe(
      map(r => r.unread || 0), catchError(() => of(0)),
    ).subscribe(n => this.unread$.next(n));
  }

  consent(): Observable<WhatsAppConsent | null> {
    if (!this.loggedIn) return of(null);
    return this.http.get<WhatsAppConsent>(`${this.base}whatsapp/consent/`, { headers: this.headers() })
      .pipe(catchError(() => of(null)));
  }

  setConsent(optedIn: boolean, phone?: string): Observable<WhatsAppConsent> {
    const body: any = { opted_in: optedIn, channel: 'web' };
    if (phone) body.phone = phone;
    return this.http.put<WhatsAppConsent>(`${this.base}whatsapp/consent/`, body, { headers: this.headers() });
  }

  /** Error body → something the UI can show. */
  static errorOf(err: any): InquiryError {
    const e = err?.error || {};
    return {
      code: e.code || (err?.status === 401 || err?.status === 403 ? 'LOGIN' : 'ERROR'),
      detail: e.detail || 'משהו השתבש, נסו שוב',
    };
  }

  /**
   * Opens the "send a message" dialog on top of the page. It is attached to
   * <body>, so it works from any card (cards sit in transformed carousels,
   * where a position:fixed child would be clipped) and never bubbles a click
   * into the card that opened it.
   */
  open(target: InquiryTarget): void {
    this.close();
    import('../components/inquiry-dialog/inquiry-dialog.component').then(({ InquiryDialogComponent }) => {
      const ref = createComponent(InquiryDialogComponent, { environmentInjector: this.injector });
      ref.instance.target = target;
      ref.instance.closed.subscribe(() => this.close());
      this.appRef.attachView(ref.hostView);
      document.body.appendChild(ref.location.nativeElement);
      this.dialogRef = ref;
    });
  }

  close(): void {
    if (this.dialogRef) {
      this.appRef.detachView(this.dialogRef.hostView);
      this.dialogRef.destroy();
      this.dialogRef = undefined;
    }
  }

  /** The old behaviour (feature off / site ads): open WhatsApp with the listing's phone. */
  static openWaMe(phone: string | undefined, adName: string): boolean {
    const clean = String(phone || '').replace(/[^0-9]/g, '');
    if (!clean) return false;
    const number = clean.startsWith('0') ? '972' + clean.substring(1) : clean;
    const msg = encodeURIComponent(`שלום, אני מתעניין ב: ${adName}`);
    window.open(`https://wa.me/${number}?text=${msg}`, '_blank');
    return true;
  }
}
